import React, { useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Bug,
  ChevronRight,
  LogOut,
  Menu,
  Moon,
  Repeat2,
  Sun,
  Smartphone,
  X,
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { useRecadastramentoResumo } from '../../hooks/useRecadastramentoResumo';
import { useTheme } from '../../contexts/ThemeContext';
import useAuditLog from '../../hooks/useAuditLog';
import useModalBehavior from '../../hooks/useModalBehavior';
import { buildServerNavigation, groupServerNavigation } from '../../config/serverNavigation';
import { buildMunicipioInstallUrl, isStandaloneDisplayMode } from '../../utils/municipioContext';

const isItemActive = (pathname, item) => {
  if (item.path === '/dashboard') return pathname === '/dashboard';
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
};

const MobileBottomNavigation = () => {
  const {
    user,
    logout,
    vinculos,
    vinculoSelecionado,
    limparVinculoSelecionado,
  } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { logAction } = useAuditLog();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const municipioId = vinculoSelecionado?.municipioId || user?.municipioId || '';
  const canOfferInstall = Boolean(municipioId) && !isStandaloneDisplayMode();

  const {
    recadastramentoData,
    isVinculoElegivel: isRecadastramentoElegivel,
  } = useRecadastramentoResumo({ user, vinculo: vinculoSelecionado });
  const recadastramentoStatus = recadastramentoData?.status || null;

  const items = useMemo(() => buildServerNavigation({
    permissions: user?.serverPortalPermissions || {},
    vinculoSelecionado,
    recadastramentoElegivel: isRecadastramentoElegivel,
    recadastramentoStatus,
  }), [user?.serverPortalPermissions, vinculoSelecionado, isRecadastramentoElegivel, recadastramentoStatus]);

  const primaryItems = useMemo(() => items.filter((item) => item.mobilePrimary), [items]);
  const secondaryGroups = useMemo(() => groupServerNavigation(items.filter((item) => !item.mobilePrimary)), [items]);

  const closeMore = () => setMoreOpen(false);
  const sheetRef = useModalBehavior({ isOpen: moreOpen, onClose: closeMore, closeOnEscape: true });

  const handleLogout = async () => {
    closeMore();
    await logAction('LOGOUT', {
      entityType: 'AUTHENTICATION',
      entityId: user?.uid || 'N/A',
      modulo: 'Portal do Servidor',
      pagina: 'Navegação móvel',
      tela: 'Menu Mais',
      tipoAcao: 'autenticacao',
      alterouDados: false,
      descricao: 'Servidor encerrou a sessão pela navegação móvel.',
    });

    logout();
    navigate('/login');
  };

  const handleSwitchVinculo = () => {
    closeMore();
    limparVinculoSelecionado();
  };

  const handleOpenTechnicalSupport = () => {
    closeMore();
    window.dispatchEvent(new CustomEvent('sisfol:open-support', {
      detail: { route: location.pathname, message: 'Quero informar um problema no sistema.' },
    }));
  };

  const handleOpenInstall = () => {
    const installUrl = buildMunicipioInstallUrl(municipioId);
    closeMore();
    if (installUrl) window.location.assign(installUrl);
  };

  return (
    <>
      <nav
        className="server-bottom-nav fixed inset-x-0 bottom-0 z-[70] border-t border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface)] backdrop-blur-xl lg:hidden"
        aria-label="Navegação principal"
      >
        <div className="mx-auto grid min-h-[4.5rem] max-w-xl items-stretch px-1" style={{ gridTemplateColumns: `repeat(${Math.max(primaryItems.length + 1, 1)}, minmax(0, 1fr))` }}>
          {primaryItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.id}
                to={item.path}
                end={item.path === '/dashboard'}
                state={item.state}
                className={({ isActive }) => `relative flex min-h-[4.5rem] min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-0.5 text-[10px] font-bold tracking-[-0.02em] transition-colors ${isActive
                  ? 'text-blue-700 dark:text-blue-300'
                  : 'text-gray-500 active:bg-gray-100 dark:text-gray-400 dark:active:bg-gray-800'}`}
                aria-label={item.label}
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute inset-x-4 top-0 h-0.5 rounded-full bg-blue-600 dark:bg-blue-400" aria-hidden="true" />}
                    <Icon size={22} strokeWidth={isActive ? 2.4 : 2} aria-hidden="true" />
                    <span className="max-w-full whitespace-nowrap leading-none">{item.shortLabel}</span>
                  </>
                )}
              </NavLink>
            );
          })}

          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className={`relative flex min-h-[4.5rem] min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1 text-[11px] font-bold transition-colors ${moreOpen
              ? 'text-blue-700 dark:text-blue-300'
              : 'text-gray-500 active:bg-gray-100 dark:text-gray-400 dark:active:bg-gray-800'}`}
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            aria-label="Abrir mais opções"
          >
            {moreOpen && <span className="absolute inset-x-4 top-0 h-0.5 rounded-full bg-blue-600 dark:bg-blue-400" aria-hidden="true" />}
            <Menu size={22} aria-hidden="true" />
            <span>Mais</span>
          </button>
        </div>
      </nav>

      {moreOpen && (
        <div
          className="server-backdrop-enter fixed inset-0 z-[85] flex items-end bg-black/45 backdrop-blur-[2px] lg:hidden"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeMore();
          }}
        >
          <section
            ref={sheetRef}
            tabIndex={-1}
            className="server-mobile-sheet server-mobile-sheet-enter max-h-[86dvh] w-full overflow-hidden rounded-t-[1.75rem] border-t border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface)] shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="server-mobile-more-title"
          >
            <header className="flex items-start justify-between gap-4 border-b border-[var(--sisdp-color-border)] px-5 pb-4 pt-5">
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-600 dark:text-blue-300">Portal do Servidor</p>
                <h2 id="server-mobile-more-title" className="mt-1 truncate text-xl font-black text-[var(--sisdp-color-text)]">Mais</h2>
                <p className="mt-1 truncate text-sm font-semibold text-[var(--sisdp-color-text-muted)]">
                  {user?.name || user?.nome || 'Servidor'}{vinculoSelecionado?.matricula ? ` · Mat. ${vinculoSelecionado.matricula}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={closeMore}
                className="server-touch-target inline-flex items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                aria-label="Fechar mais opções"
              >
                <X size={22} />
              </button>
            </header>

            <div className="sisfol-soft-scrollbar max-h-[calc(86dvh-7.5rem)] overflow-y-auto px-4 py-4">
              {secondaryGroups.map((group) => (
                <div key={group.category} className="mb-5 last:mb-2">
                  <h3 className="mb-2 px-2 text-xs font-extrabold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">{group.category}</h3>
                  <div className="overflow-hidden rounded-2xl border border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface)]">
                    {group.items.map((item, index) => {
                      const Icon = item.icon;
                      const active = isItemActive(location.pathname, item);
                      return (
                        <NavLink
                          key={item.id}
                          to={item.path}
                          state={item.state}
                          onClick={closeMore}
                          className={`flex min-h-14 items-center gap-3 px-4 py-3 text-sm font-bold transition-colors ${index > 0 ? 'border-t border-[var(--sisdp-color-border)]' : ''} ${active
                            ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200'
                            : 'text-[var(--sisdp-color-text)] hover:bg-gray-50 dark:hover:bg-gray-800/70'}`}
                        >
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                            <Icon size={20} aria-hidden="true" />
                          </span>
                          <span className="min-w-0 flex-1 truncate">{item.label}</span>
                          <ChevronRight size={18} className="shrink-0 text-gray-400" aria-hidden="true" />
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              ))}

              {canOfferInstall && (
                <button
                  type="button"
                  onClick={handleOpenInstall}
                  className="mb-3 flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-4 text-sm font-bold text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-200"
                >
                  <Smartphone size={18} aria-hidden="true" />
                  Usar no celular
                </button>
              )}

              <button
                type="button"
                onClick={handleOpenTechnicalSupport}
                className="mb-3 flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl border border-violet-200 bg-violet-50 px-4 text-sm font-bold text-violet-700 dark:border-violet-900/60 dark:bg-violet-950/30 dark:text-violet-200"
              >
                <Bug size={18} aria-hidden="true" />
                Informar problema no sistema
              </button>

              <div className="mb-3 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface)] px-3 text-sm font-bold text-[var(--sisdp-color-text)]"
                >
                  {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
                  {theme === 'light' ? 'Tema escuro' : 'Tema claro'}
                </button>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-3 text-sm font-bold text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200"
                >
                  <LogOut size={18} />
                  Sair
                </button>
              </div>

              {Array.isArray(vinculos) && vinculos.length > 1 && (
                <button
                  type="button"
                  onClick={handleSwitchVinculo}
                  className="mb-2 flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-4 text-sm font-bold text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-200"
                >
                  <Repeat2 size={18} />
                  Trocar vínculo
                </button>
              )}
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default MobileBottomNavigation;
